import { create } from "zustand";
import { useState, useEffect, useCallback, useRef } from "react";
import type { SsoClaims } from "@rc-acs/shared/types";
import { isSessionExpired, timeUntilExpiry } from "../lib/session.js";

// ── Zustand store (used by App.tsx for global auth state) ──────────────

interface AuthState {
  user: SsoClaims | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  login: () => void;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  checkAuth: async () => {
    try {
      const profileRes = await fetch("/api/sso/profile", {
        credentials: "include",
        headers: { "Accept": "application/json" },
      });
      if (profileRes.ok) {
        const user = await profileRes.json();
        set({ user, isAuthenticated: true, isLoading: false });
        return;
      }

      const refreshRes = await fetch("/api/sso/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (refreshRes.ok) {
        const retryProfileRes = await fetch("/api/sso/profile", {
          credentials: "include",
          headers: { "Accept": "application/json" },
        });
        if (retryProfileRes.ok) {
          const user = await retryProfileRes.json();
          set({ user, isAuthenticated: true, isLoading: false });
          return;
        }
      }

      set({ user: null, isAuthenticated: false, isLoading: false });
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  login: () => {
    window.location.href = "/api/sso/login";
  },

  logout: async () => {
    try {
      await fetch("/api/sso/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Even if logout API fails, clear local state
    } finally {
      set({ user: null, isAuthenticated: false, isLoading: false });
      window.location.href = "/login";
    }
  },
}));

// ── useAuth React hook (for Profile page and auth-aware components) ────

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  role: string;
  engine_scope: string[];
  expiresAt: number;
}

const REFRESH_THRESHOLD_SECONDS = 3600; // 1h before expiry
const CHECK_INTERVAL_MS = 60000; // check every 60s

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshingRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    const res = await fetch("/api/sso/profile", { credentials: "include" });
    if (!res.ok) {
      setUser(null);
      setIsLoading(false);
      return null;
    }
    const data: AuthUser = await res.json();
    setUser(data);
    setIsLoading(false);
    return data;
  }, []);

  const refreshSession = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const res = await fetch("/api/sso/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        setUser(null);
        return;
      }
      const profile = await fetchProfile();
      return profile;
    } finally {
      refreshingRef.current = false;
    }
  }, [fetchProfile]);

  const login = useCallback((returnUrl: string = "/") => {
    const encoded = encodeURIComponent(returnUrl);
    window.location.href = `/api/sso/login?return=${encoded}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/sso/logout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } finally {
      setUser(null);
      window.location.href = "/login";
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (user && !isSessionExpired(user.expiresAt) && timeUntilExpiry(user.expiresAt) <= REFRESH_THRESHOLD_SECONDS) {
        refreshSession();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, refreshSession]);

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshSession,
  };
}
